import { Formik } from "formik";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import { Creators as CattleProducerActions } from "../../../store/ducks/cattleProducer";

// Components
import {
  Button,
  Col,
  DatePicker,
  Icon,
  Input,
  notification,
  Radio,
  Row,
  Select,
  Spin,
  Upload,
} from "antd";
import ButtonStandard from "../../../components/utils/button";
import CustomDivider from "../../../components/utils/customDivider";
import SelectCattleProducer from "../../../components/utils/selectCattleProducer";
import ArrobaProductionCard from "./components/ArrobaProductionCard";
import DailyContractCard from "./components/DailyContractCard";
import DryMatterConsumeCard from "./components/DryMatterConsumeCard";
import HerdFormDrawer from "./components/HerdFormDrawer";
import HerdsTable from "./components/HerdsTable";
import PartnershipCard from "./components/PartnershipCard";
import { Container, Footer, Title } from "./styles";

// Services
import BreadCrumbs from "../../../components/utils/breadCrumbs";
import RadioButton from "../../../components/utils/radioButton";
import {
  showBoitelContract,
  storeBoitelContract,
  updateBoitelContract,
} from "../../../services/boitelContractService";
import { getBreedIndexActive } from "../../../services/breedService";
import { getCattleProducerShow } from "../../../services/cattleProducerService";
import { getSupplementIndexDropDown } from "../../../services/supplementService";
import { logEventInfo } from "../../../utils/logger";
import InfoTooltip from "../../../components/utils/infoTooltip";

const { Option } = Select;

const dateFormat = "DD/MM/YYYY";

const CONTRACT_TYPES = [
  { value: "daily", label: "Diária" },
  { value: "dryMatterConsume", label: "Cons. MS" },
  { value: "arrobaProduction", label: "@ Produz." },
  { value: "partnership", label: "Parceria" },
];

const validationSchema = Yup.object({
  identificationContract: Yup.string().trim().required(),
  contractDate: Yup.date().required(),
  initialDateContract: Yup.date().required(),
  cattleProducerId: Yup.string().required(),
  multipleFarm: Yup.boolean().required(),
  cattleProducerFarmId: Yup.string().when("multipleFarm", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required(),
  }),
  herds: Yup.array()
    .of(
      Yup.object().shape({
        amountAnimals: Yup.number().min(1).required(),
        breedId: Yup.string().required(),
        weightAverage: Yup.number().min(0).required(),
        useSisbov: Yup.boolean().required(),
        trace: Yup.boolean().required(),
        entryDate: Yup.date().required(),
        cattleProducerFarmId: Yup.string().nullable(),
      }),
    )
    .min(1)
    .required(),
  contractType: Yup.string().required(),
  dailyContract: Yup.object().when("contractType", {
    is: "daily",
    then: (schema) =>
      schema.shape({
        dailyValue: Yup.number().min(1).required(),
        herdsWithDays: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required(),
            numberOfDays: Yup.number().min(1).required(),
          }),
        ),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  dryMatterConsumeContract: Yup.object().when("contractType", {
    is: "dryMatterConsume",
    then: (schema) =>
      schema.shape({
        measurementType: Yup.string().required(),
        reportType: Yup.string().required(),
        herdsWithData: Yup.array().when("reportType", {
          is: "cjto",
          then: (schema) =>
            schema
              .of(
                Yup.object().shape({
                  numberOfDays: Yup.number().when("measurementType", {
                    is: "pre_fixado",
                    then: (schema) => schema.min(1).required(),
                    otherwise: (schema) => schema.nullable(),
                  }),
                  averageOffer: Yup.number().min(0).required(),
                  costPerKilogramDryMatter: Yup.number().min(0).required(),
                }),
              )
              .min(1)
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
        uniqueData: Yup.object().when("reportType", {
          is: "unico",
          then: (schema) =>
            schema
              .shape({
                averageOffer: Yup.number().min(0).required(),
                numberOfDays: Yup.number().when("measurementType", {
                  is: "pre_fixado",
                  then: (schema) => schema.min(1).required(),
                  otherwise: (schema) => schema.nullable(),
                }),
                costPerKilogramDryMatter: Yup.number().min(0).required(),
              })
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  arrobaProductionContract: Yup.object().when("contractType", {
    is: "arrobaProduction",
    then: (schema) =>
      schema.shape({
        reportType: Yup.string().required(),
        herdsWithData: Yup.array().when("reportType", {
          is: "cjto",
          then: (schema) =>
            schema
              .of(
                Yup.object().shape({
                  carcassHarnessingPercentage: Yup.number().required(),
                }),
              )
              .min(1)
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
        uniqueData: Yup.object().when("reportType", {
          is: "unico",
          then: (schema) =>
            schema
              .shape({
                carcassHarnessingPercentage: Yup.number().required(),
              })
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
        valuePerArroba: Yup.number().min(0).required(),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  partnershipContract: Yup.object().when("contractType", {
    is: "partnership",
    then: (schema) =>
      schema.shape({
        partnershipType: Yup.string().required(),
        reportType: Yup.string().required(),
        herdsWithData: Yup.array().when("reportType", {
          is: "cjto",
          then: (schema) =>
            schema
              .of(
                Yup.object().shape({
                  carcassHarnessingPercentage: Yup.number().required(),
                  numberOfDays: Yup.number().when("partnershipType", {
                    is: "ARROBA_PRODUCTION",
                    then: (schema) => schema.min(1).required(),
                    otherwise: (schema) => schema.nullable(),
                  }),
                }),
              )
              .min(1)
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
        uniqueData: Yup.object().when("reportType", {
          is: "unico",
          then: (schema) =>
            schema
              .shape({
                carcassHarnessingPercentage: Yup.number().required(),
                numberOfDays: Yup.number().when("partnershipType", {
                  is: "ARROBA_PRODUCTION",
                  then: (schema) => schema.min(1).required(),
                  otherwise: (schema) => schema.nullable(),
                }),
              })
              .required(),
          otherwise: (schema) => schema.nullable(),
        }),
        partnerPercentage: Yup.number().when("partnershipType", {
          is: "ARROBA_PRODUCTION",
          then: (schema) => schema.min(0).max(100).required(),
          otherwise: (schema) => schema.nullable(),
        }),
        agreedWeightType: Yup.string().when("partnershipType", {
          is: "CARCASS_HARNESSING",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable(),
        }),
        maxBreakPercentage: Yup.number().when("partnershipType", {
          is: "CARCASS_HARNESSING",
          then: (schema) => schema.min(0).required(),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
}).test(
  "herds-farm-required",
  "Fazenda é obrigatória quando múltiplas fazendas estão habilitadas",
  function (values) {
    if (values.multipleFarm) {
      const herdsWithoutFarm = (values.herds || []).filter(
        (herd) => !herd.cattleProducerFarmId,
      );
      if (herdsWithoutFarm.length > 0) {
        return this.createError({
          path: "herds",
          message: "Todas as fazendas devem ser preenchidas",
        });
      }
    }
    return true;
  },
);

const INITIAL_FORM_VALUES = {
  identificationContract: "",
  contractDate: null,
  initialDateContract: null,
  fileUrl: null,
  cattleProducerId: null,
  multipleFarm: false,
  cattleProducerFarmId: null,
  herds: [],
  contractType: null,
  dailyContract: {
    dailyValue: null,
    protocolValue: null,
    fixedValue: null,
    dietValue: null,
    otherValue: null,
    observation: null,
    herdsWithDays: [],
  },
  dryMatterConsumeContract: {
    measurementType: null,
    reportType: null,
    herdsWithData: [],
    uniqueData: {
      averageOffer: null,
      numberOfDays: null,
      costPerKilogramDryMatter: null,
    },
  },
  arrobaProductionContract: {
    reportType: null,
    herdsWithData: [],
    uniqueData: {
      carcassHarnessingPercentage: null,
    },
    valuePerArroba: null,
  },
  partnershipContract: {
    reportType: null,
    partnershipType: "CARCASS_HARNESSING",
    partnerPercentage: null,
    herdsWithData: [],
    uniqueData: {
      carcassHarnessingPercentage: null,
      numberOfDays: null,
    },
    agreedWeightType: null,
    maxBreakPercentage: null,
  },
};

const BoitelContractsForm = () => {
  const formRef = useRef(null);
  const history = useHistory();
  const { id } = useParams();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const {
    isCattleProducerListDrowpDownLoading,
    listDrowpDown: cattleProducersList,
  } = useSelector((state) => state.cattleProducer);

  const dispatch = useDispatch();

  const [form, setForm] = useState(INITIAL_FORM_VALUES);

  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [breedsList, setBreedsList] = useState([]);
  const [supplementsList, setSupplementsList] = useState([]);
  const [cattleProducerProperties, setCattleProducerProperties] = useState([]);
  const [formHerd, setFormHerd] = useState(null);
  const [isHerdFormVisible, setIsHerdFormVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [herds, setHerds] = useState([]);

  const handleCattleProducerChange = useCallback(
    (value) => {
      formRef.current.setFieldValue("cattleProducerId", value);
      formRef.current.setFieldValue("cattleProducerFarmId", null);
      if (value) {
        setCattleProducerProperties(
          cattleProducersList.find((cp) => cp.id === value).properties || [],
        );
      } else {
        setCattleProducerProperties([]);
      }
    },
    [cattleProducersList],
  );

  const handleAddHerd = useCallback(
    (formikValues) => {
      if (!formikValues.multipleFarm && !formikValues.cattleProducerFarmId) {
        notification.error({
          message:
            translation.boitelContractsPage.form.cattleProducerFarmRequired,
        });
        return;
      }
      if (formikValues.multipleFarm && !formikValues.cattleProducerId) {
        notification.error({
          message: translation.boitelContractsPage.form.cattleProducerRequired,
        });
        return;
      }
      setFormHerd({
        id: uuid(),
        amountAnimals: null,
        breedId: null,
        weightAverage: null,
        useSisbov: false,
        trace: false,
        entryDate: null,
        cattleProducerFarmId: null,
        supplementId: null,
      });
      setIsHerdFormVisible(true);
    },
    [
      translation.boitelContractsPage.form.cattleProducerFarmRequired,
      translation.boitelContractsPage.form.cattleProducerRequired,
    ],
  );

  const handleEditHerd = useCallback((herd) => {
    setFormHerd(herd);
    setIsHerdFormVisible(true);
  }, []);

  const handleRemoveHerd = useCallback(
    (herd) => {
      formRef.current.setFieldValue(
        "herds",
        form.herds.filter((h) => h.id !== herd.id),
      );
      setHerds(form.herds.filter((h) => h.id !== herd.id));
      setForm((prev) => ({
        ...prev,
        herds: prev.herds.filter((h) => h.id !== herd.id),
      }));
    },
    [form.herds],
  );

  const handleSaveHerd = useCallback(
    (herdValues) => {
      const breed = breedsList.find((b) => b.id === herdValues.breedId);
      const farm = cattleProducerProperties.find(
        (f) => f.id === herdValues.cattleProducerFarmId,
      );
      const supplement = supplementsList.find(
        (s) => s.id === herdValues.supplementId,
      );

      const herdData = {
        ...herdValues,
        breedName: breed?.name || "",
        farmName: farm?.name || "",
        supplementName: supplement?.name || "",
      };

      const existingIndex = herds.findIndex((h) => h.id === herdData.id);
      if (existingIndex >= 0) {
        const newHerds = [...herds];
        newHerds[existingIndex] = herdData;
        formRef.current.setFieldValue("herds", newHerds);
        setHerds(newHerds);
      } else {
        formRef.current.setFieldValue("herds", [...herds, herdData]);
        setHerds([...herds, herdData]);
      }
      setIsHerdFormVisible(false);
      setFormHerd(null);
    },
    [herds, formRef, breedsList, cattleProducerProperties, supplementsList],
  );

  const trasnformValuesIntoDTORequest = useCallback((values) => {
    // Calcular total de animais
    const amountAnimals = (values.herds || []).reduce(
      (sum, herd) => sum + (herd.amountAnimals || 0),
      0,
    );

    // Determinar cattleProducerPropertyId (quando não é múltiplas fazendas, usa o cattleProducerFarmId)
    const cattleProducerPropertyId =
      !values.multipleFarm && values.cattleProducerFarmId
        ? values.cattleProducerFarmId
        : null;

    // Transformar herds para o formato DTO
    const herdsDTO = (values.herds || []).map((herd) => {
      const herdDTO = {
        id: herd.id || null,
        cattleProducerPropertyId:
          herd.cattleProducerPropertyId || herd.cattleProducerFarmId || null,
        amountAnimals: herd.amountAnimals || 0,
        supplementId: herd.supplementId || null,
        breedId: herd.breedId || null,
        weightAverage: herd.weightAverage || null,
        entryDate: herd.entryDate
          ? moment(herd.entryDate).format("YYYY-MM-DD")
          : null,
        effectiveEntryDate: null,
        gender: herd.gender || null,
        useSisbov: herd.useSisbov || false,
        trace: herd.trace || false,
        finalDate: null,
        effectiveFinalDate: null,
        effectiveNumberOfDays: herd.effectiveNumberOfDays || null,
        averageOffer: herd.averageOffer || null,
        numberOfDays: herd.numberOfDays || null,
        costPerKilogramDryMatter: herd.costPerKilogramDryMatter || null,
        carcassHarnessing: herd.carcassHarnessingPercentage || null,
      };

      // Para contrato diária, buscar numberOfDays de herdsWithDays
      if (
        values.contractType === "daily" &&
        values.dailyContract?.herdsWithDays
      ) {
        const herdWithDays = values.dailyContract.herdsWithDays.find(
          (h) => h.id === herd.id,
        );
        if (herdWithDays) {
          herdDTO.numberOfDays = herdWithDays.numberOfDays || null;
        }
      }

      return herdDTO;
    });

    // Construir objeto DTO base
    const dto = {
      id: values.id || null,
      identificationContract: values.identificationContract || null,
      cattleProducerId: values.cattleProducerId || null,
      cattleProducerPropertyId: cattleProducerPropertyId,
      multipleFarm: values.multipleFarm || false,
      contractDate: values.contractDate
        ? moment(values.contractDate).format("YYYY-MM-DD")
        : null,
      initialDateContract: values.initialDateContract
        ? moment(values.initialDateContract).format("YYYY-MM-DD")
        : null,
      finalDateContract: null,
      closureDateContract: null,
      contractType: values.contractType,
      amountAnimals: amountAnimals,
      protocolValue: null,
      fixedValue: null,
      dietValue: null,
      otherValue: null,
      dailyValue: null,
      observation: null,
      measurementType: null,
      reportType: null,
      averageOffer: null,
      numberOfDays: null,
      costPerKilogramDryMatter: null,
      carcassHarnessing: null,
      valuePerArroba: null,
      agreedWeightType: null,
      maxBreakPercentage: null,
      estimatedBalance: values.estimatedBalance || null,
      currentBalance: values.currentBalance || null,
      closureBalance: values.closureBalance || null,
      finalBalance: values.finalBalance || null,
      herds: herdsDTO,
    };

    // Preencher campos específicos baseado no tipo de contrato
    if (values.contractType === "daily" && values.dailyContract) {
      dto.protocolValue = values.dailyContract.protocolValue || null;
      dto.fixedValue = values.dailyContract.fixedValue || null;
      dto.dietValue = values.dailyContract.dietValue || null;
      dto.otherValue = values.dailyContract.otherValue || null;
      dto.dailyValue = values.dailyContract.dailyValue || null;
      dto.observation = values.dailyContract.observation || null;
    }

    if (
      values.contractType === "dryMatterConsume" &&
      values.dryMatterConsumeContract
    ) {
      dto.measurementType =
        values.dryMatterConsumeContract.measurementType || null;
      dto.reportType = values.dryMatterConsumeContract.reportType || null;

      // Se reportType é "unico", usar uniqueData
      if (
        values.dryMatterConsumeContract.reportType === "unico" &&
        values.dryMatterConsumeContract.uniqueData
      ) {
        dto.averageOffer =
          values.dryMatterConsumeContract.uniqueData.averageOffer || null;
        dto.numberOfDays =
          values.dryMatterConsumeContract.uniqueData.numberOfDays || null;
        dto.costPerKilogramDryMatter =
          values.dryMatterConsumeContract.uniqueData.costPerKilogramDryMatter ||
          null;
      }
      // Se reportType é "cjto", os dados já estão nos herds (já mapeados acima)
    }

    if (
      values.contractType === "arrobaProduction" &&
      values.arrobaProductionContract
    ) {
      dto.reportType = values.arrobaProductionContract.reportType || null;
      dto.valuePerArroba =
        values.arrobaProductionContract.valuePerArroba || null;

      // Se reportType é "unico", usar uniqueData para carcassHarnessing
      if (
        values.arrobaProductionContract.reportType === "unico" &&
        values.arrobaProductionContract.uniqueData
      ) {
        dto.carcassHarnessing =
          values.arrobaProductionContract.uniqueData
            .carcassHarnessingPercentage || null;
      }
      // Se reportType é "cjto", os dados já estão nos herds (já mapeados acima)
    }

    if (values.contractType === "partnership" && values.partnershipContract) {
      dto.partnershipType = values.partnershipContract.partnershipType || null;
      dto.partnerPercentage =
        values.partnershipContract.partnerPercentage || null;
      dto.reportType = values.partnershipContract.reportType || null;
      dto.agreedWeightType =
        values.partnershipContract.agreedWeightType || null;
      dto.maxBreakPercentage =
        values.partnershipContract.maxBreakPercentage ?? null;
      if (
        values.partnershipContract.reportType === "unico" &&
        values.partnershipContract.uniqueData
      ) {
        dto.carcassHarnessing =
          values.partnershipContract.uniqueData.carcassHarnessingPercentage ||
          null;
        dto.numberOfDays =
          values.partnershipContract.uniqueData.numberOfDays || null;
      }
    }

    return dto;
  }, []);

  const handleSubmitForm = async (values, actions) => {
    if (
      values.contractType === "arrobaProduction" ||
      values.contractType === "partnership"
    ) {
      const contract =
        values.contractType === "arrobaProduction"
          ? values.arrobaProductionContract
          : values.partnershipContract;
      const reportType = contract?.reportType;
      let percentages = [];
      if (reportType === "unico") {
        const v = contract?.uniqueData?.carcassHarnessingPercentage;
        if (v != null && v !== "") percentages.push(Number(v));
      } else if (reportType === "cjto") {
        (values.herds || []).forEach((h) => {
          const v = h.carcassHarnessingPercentage;
          if (v != null && v !== "") percentages.push(Number(v));
        });
      }
      const outOfRange = percentages.some((p) => p < 40 || p > 70);
      if (outOfRange) {
        notification.error({
          message:
            translation.boitelContractsPage.form.carcassPercentageRangeError,
        });
        if (values.contractType === "arrobaProduction") {
          if (values.arrobaProductionContract?.reportType === "unico") {
            actions.setErrors({
              arrobaProductionContract: {
                uniqueData: {
                  carcassHarnessingPercentage:
                    translation.boitelContractsPage.form
                      .carcassPercentageRangeError,
                },
              },
            });
          } else if (values.arrobaProductionContract?.reportType === "cjto") {
            actions.setErrors({
              arrobaProductionContract: {
                herdsWithData: [
                  {
                    carcassHarnessingPercentage:
                      translation.boitelContractsPage.form
                        .carcassPercentageRangeError,
                  },
                ],
              },
            });
          }
        } else if (values.contractType === "partnership") {
          if (values.partnershipContract?.reportType === "unico") {
            actions.setErrors({
              partnershipContract: {
                uniqueData: {
                  carcassHarnessingPercentage:
                    translation.boitelContractsPage.form
                      .carcassPercentageRangeError,
                },
              },
            });
          } else if (values.partnershipContract?.reportType === "cjto") {
            actions.setErrors({
              partnershipContract: {
                herdsWithData: [
                  {
                    carcassHarnessingPercentage:
                      translation.boitelContractsPage.form
                        .carcassPercentageRangeError,
                  },
                ],
              },
            });
          }
        }
        return;
      }
    }

    setIsLoadingRequest(true);
    try {
      const submitData = {
        ...values,
        contractDate: values.contractDate
          ? moment(values.contractDate).format("YYYY-MM-DD")
          : null,
        initialDateContract: values.initialDateContract
          ? moment(values.initialDateContract).format("YYYY-MM-DD")
          : null,
        herds: (values.herds || []).map((herd) => ({
          ...herd,
          entryDate: herd.entryDate
            ? moment(herd.entryDate).format("YYYY-MM-DD")
            : null,
        })),
        dailyContract: null,
        dryMatterConsumeContract: null,
        arrobaProductionContract: null,
        partnershipContract: null,
      };

      // Incluir apenas o objeto de contrato do tipo selecionado
      if (values.contractType === "daily") {
        submitData.dailyContract = values.dailyContract;
      } else if (values.contractType === "dryMatterConsume") {
        submitData.dryMatterConsumeContract = values.dryMatterConsumeContract;
      } else if (values.contractType === "arrobaProduction") {
        submitData.arrobaProductionContract = values.arrobaProductionContract;
      } else if (values.contractType === "partnership") {
        submitData.partnershipContract = values.partnershipContract;
      }

      logEventInfo(
        "BoitelContractForm - handleSubmitForm - submitData",
        submitData,
      );

      const fileToSend = fileList.length > 0 ? fileList[0] : null;
      const isNewFile = fileToSend instanceof File;

      const body = trasnformValuesIntoDTORequest(submitData);
      if (id && !isNewFile) body.keepExistingFile = true;
      logEventInfo("BoitelContractForm - handleSubmitForm - body", body);

      let formData = new FormData();
      formData.append(
        "request",
        new Blob([JSON.stringify(body)], { type: "application/json" }),
      );
      // Na edição, só envia file quando for um novo arquivo (File); se for o objeto de exibição do arquivo existente, não envia para preservar o arquivo atual
      if (id) {
        if (isNewFile) formData.append("file", fileToSend);
      } else {
        formData.append("file", fileToSend);
      }

      logEventInfo(
        "BoitelContractForm - handleSubmitForm - formData",
        formData,
      );

      if (id) {
        await updateBoitelContract({
          groupId,
          farmId,
          id,
          body: formData,
        });
        notification.success({
          message: translation.boitelContractsPage.form.successUpdate,
        });
      } else {
        await storeBoitelContract({
          groupId,
          farmId,
          body: formData,
        });
        notification.success({
          message: translation.boitelContractsPage.form.successCreate,
        });
      }
      history.push("/admin/boitel/contracts");
    } catch (error) {
      notification.error({
        message: translation.boitelContractsPage.form.errorSave,
      });
    } finally {
      setIsLoadingRequest(false);
    }
  };

  const handleCancel = () => {
    history.push("/admin/boitel/contracts");
  };

  const uploadProps = {
    accept:
      ".pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
  };

  useEffect(() => {
    async function fetchBreeds() {
      try {
        const {
          data: { results },
        } = await getBreedIndexActive({
          groupId,
          farmId,
          withoutBreedCurveParam: true,
        });
        setBreedsList(results || []);
      } catch (error) {
        setBreedsList([]);
      }
    }
    fetchBreeds();
  }, [groupId, farmId]);

  useEffect(() => {
    async function fetchCattleProducers() {
      dispatch(
        CattleProducerActions.getDropdownCattleProducers(groupId, farmId),
      );
    }
    fetchCattleProducers();
  }, [groupId, farmId, dispatch]);

  useEffect(() => {
    async function fetchSupplements() {
      try {
        const {
          data: { results },
        } = await getSupplementIndexDropDown({
          groupId,
          farmId,
        });
        setSupplementsList(results || []);
      } catch (error) {
        setSupplementsList([]);
      }
    }
    fetchSupplements();
  }, [groupId, farmId]);

  useEffect(() => {
    if (id) {
      setIsLoadingData(true);
      async function fetchContract() {
        try {
          const response = await showBoitelContract({
            groupId,
            farmId,
            id,
          });
          const contractData = response?.data?.results || {};
          let cattleProducerProperties = [];
          if (contractData.cattleProducerId) {
            const cattleProducer = await getCattleProducerShow({
              groupId,
              farmId,
              id: contractData.cattleProducerId,
            });
            cattleProducerProperties =
              cattleProducer.data.results.properties || [];
          } else {
            cattleProducerProperties = [];
          }
          logEventInfo(
            "BoitelContractForm - fetchContract - cattleProducerProperties",
            cattleProducerProperties,
          );
          logEventInfo(
            "BoitelContractForm - fetchContract - contractData",
            contractData,
          );
          const newForm = {
            ...contractData,
            cattleProducerFarmId: contractData.cattleProducerPropertyId || null,
            contractDate: contractData.contractDate
              ? moment(contractData.contractDate)
              : null,
            initialDateContract: contractData.initialDateContract
              ? moment(contractData.initialDateContract)
              : null,
            herds: (contractData.herds || []).map((herd) => ({
              ...herd,
              carcassHarnessingPercentage: herd.carcassHarnessing || null,
              cattleProducerFarmId: herd.cattleProducerPropertyId || null,
              farmName: herd.cattleProducerPropertyName || null,
              supplementName: herd.supplementName || null,
              entryDate: herd.entryDate ? moment(herd.entryDate) : null,
            })),
          };
          if (contractData.contractType === "daily") {
            newForm.dailyContract = {
              dailyValue: contractData.dailyValue || null,
              protocolValue: contractData.protocolValue || null,
              fixedValue: contractData.fixedValue || null,
              dietValue: contractData.dietValue || null,
              otherValue: contractData.otherValue || null,
              observation: contractData.observation || null,
              herdsWithDays:
                contractData.herds.map((herd) => {
                  return {
                    id: herd.id,
                    numberOfDays: herd.numberOfDays || null,
                  };
                }) || [],
            };
          } else if (contractData.contractType === "dryMatterConsume") {
            newForm.dryMatterConsumeContract = {
              measurementType: contractData.measurementType || null,
              reportType: contractData.reportType || null,
              herdsWithData:
                contractData.herds.map((herd) => {
                  return {
                    id: herd.id,
                    averageOffer: herd.averageOffer || null,
                    numberOfDays: herd.numberOfDays || null,
                    costPerKilogramDryMatter:
                      herd.costPerKilogramDryMatter || null,
                  };
                }) || [],
              uniqueData: contractData.carcassHarnessing
                ? {
                    carcassHarnessingPercentage:
                      contractData.carcassHarnessing || null,
                  }
                : null,
            };
          } else if (contractData.contractType === "arrobaProduction") {
            newForm.arrobaProductionContract = {
              reportType: contractData.reportType || null,
              valuePerArroba: contractData.valuePerArroba || null,
              herdsWithData:
                contractData.herds.map((herd) => {
                  return {
                    id: herd.id,
                    carcassHarnessingPercentage:
                      herd.carcassHarnessingPercentage || null,
                  };
                }) || [],
              uniqueData: contractData.carcassHarnessing
                ? {
                    carcassHarnessingPercentage:
                      contractData.carcassHarnessing || null,
                  }
                : null,
            };
          } else if (contractData.contractType === "partnership") {
            newForm.partnershipContract = {
              partnershipType: contractData.partnershipType || null,
              partnerPercentage: contractData.partnerPercentage || null,
              reportType: contractData.reportType || null,
              agreedWeightType: contractData.agreedWeightType || null,
              maxBreakPercentage: contractData.maxBreakPercentage || null,
              herdsWithData:
                contractData.herds.map((herd) => {
                  return {
                    id: herd.id,
                    carcassHarnessingPercentage: herd.carcassHarnessing || null,
                    numberOfDays: herd.numberOfDays || null,
                  };
                }) || [],
              uniqueData:
                contractData.reportType === "unico"
                  ? {
                      carcassHarnessingPercentage:
                        contractData.carcassHarnessing || null,
                      numberOfDays: contractData.numberOfDays || null,
                    }
                  : null,
            };
          }

          if (newForm.fileUrl) {
            setFileList([
              {
                uid: "-1",
                name: "Arquivo anexado",
                status: "done",
                url: newForm.fileUrl,
              },
            ]);
          } else {
            setFileList([]);
          }

          logEventInfo("BoitelContractForm - fetchContract - newForm", newForm);
          setForm(newForm);
          setHerds(newForm.herds || []);
          setCattleProducerProperties(cattleProducerProperties);
        } catch (error) {
          notification.error({
            message: translation.error.error,
            description: translation.boitelContractsPage.form.errorLoading,
          });
        } finally {
          setIsLoadingData(false);
        }
      }
      fetchContract();
    }
  }, [id, groupId, farmId, translation]);

  return (
    <Container>
      <Spin spinning={isLoadingData || isLoadingRequest}>
        <Row type="flex" justify="space-between" align="middle">
          <Col span={12}>
            <Title>
              {id
                ? translation.boitelContractsPage.form.titleEdit
                : translation.boitelContractsPage.form.titleCreate}
            </Title>
          </Col>
          <Col span={12} align="right">
            <BreadCrumbs>
              <span
                onClick={() => history.push("/admin/boitel/contracts")}
                style={{ cursor: "pointer" }}
              >
                <span className="pageTreeInative">Decisões</span>
              </span>
              <span className="pageTreeActive">Boitel - Contratos</span>
            </BreadCrumbs>
          </Col>
        </Row>
        <Formik
          ref={formRef}
          enableReinitialize={true}
          initialValues={form}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <>
              <form>
                <div className="formContent">
                  {Object.keys(errors).length > 0 && submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                  {/* Seção: Dados do Contrato */}
                  <CustomDivider
                    title={
                      translation.boitelContractsPage.form.sections.contractData
                    }
                  />

                  <Row type="flex" gutter={16}>
                    <Col span={6}>
                      <Row>
                        <label
                          className={
                            errors.identificationContract && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.boitelContractsPage.form
                              .identificationContract
                          }
                          *
                        </label>
                      </Row>
                      <Row>
                        <Input
                          value={values.identificationContract || ""}
                          placeholder={
                            translation.boitelContractsPage.form
                              .identificationContractPlaceholder
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "identificationContract",
                              e.target.value,
                            )
                          }
                        />
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <label
                          className={
                            errors.contractDate && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.boitelContractsPage.form.contractDate}*
                        </label>
                        <InfoTooltip
                          title={
                            translation.boitelContractsPage.form
                              .contractDateTooltip
                          }
                          placement="top"
                        />
                      </Row>
                      <Row>
                        <DatePicker
                          style={{ width: "100%" }}
                          value={
                            values.contractDate
                              ? moment(values.contractDate)
                              : null
                          }
                          disabledDate={(current) =>
                            current && current.isAfter(moment())
                          }
                          format={dateFormat}
                          placeholder={translation.defaultDatePickerPlaceholder}
                          onChange={(date) =>
                            setFieldValue("contractDate", date)
                          }
                        />
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <label
                          className={
                            errors.initialDateContract && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.boitelContractsPage.form
                              .initialDateContract
                          }
                          *
                        </label>
                        <InfoTooltip
                          title={
                            translation.boitelContractsPage.form
                              .initialDateContractTooltip
                          }
                          placement="top"
                        />
                      </Row>
                      <Row>
                        <DatePicker
                          style={{ width: "100%" }}
                          value={
                            values.initialDateContract
                              ? moment(values.initialDateContract)
                              : null
                          }
                          format={dateFormat}
                          placeholder={translation.defaultDatePickerPlaceholder}
                          onChange={(date) =>
                            setFieldValue("initialDateContract", date)
                          }
                        />
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <label>
                          {translation.boitelContractsPage.form.fileUrl}
                        </label>
                      </Row>
                      <Row>
                        <Upload {...uploadProps}>
                          <Button>
                            <Icon type="upload" />{" "}
                            {translation.boitelContractsPage.form.uploadFile}
                          </Button>
                        </Upload>
                      </Row>
                    </Col>
                  </Row>

                  {/* Seção: Cliente */}
                  <CustomDivider
                    title={translation.boitelContractsPage.form.sections.client}
                  />

                  <Row type="flex" gutter={16}>
                    <Col span={12}>
                      <Row>
                        <label
                          className={
                            errors.cattleProducerId && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.boitelContractsPage.form.cattleProducer}*
                        </label>
                      </Row>
                      <Row>
                        <SelectCattleProducer
                          value={values.cattleProducerId}
                          onlyWithProperties
                          onChange={handleCattleProducerChange}
                        />
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <label
                          className={
                            errors.multipleFarm && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.boitelContractsPage.form.multipleFarm}*
                        </label>
                      </Row>
                      <Row>
                        <Radio.Group
                          value={values.multipleFarm}
                          onChange={(e) =>
                            setFieldValue("multipleFarm", e.target.value)
                          }
                        >
                          <Row type="flex" gutter={8}>
                            <Col>
                              <RadioButton value={false}>Única</RadioButton>
                            </Col>
                            <Col>
                              <RadioButton value={true}>Multipla</RadioButton>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </Row>
                    </Col>
                    {!values.multipleFarm && (
                      <Col span={6}>
                        <Row>
                          <label
                            className={
                              errors.cattleProducerFarmId && submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {
                              translation.boitelContractsPage.form
                                .cattleProducerFarm
                            }
                            *
                          </label>
                        </Row>
                        <Row>
                          <Select
                            loading={isCattleProducerListDrowpDownLoading}
                            value={values.cattleProducerFarmId || undefined}
                            placeholder={translation.defaultSelectPlaceholder}
                            onChange={(value) =>
                              setFieldValue("cattleProducerFarmId", value)
                            }
                          >
                            {cattleProducerProperties.map((property) => (
                              <Option key={property.id} value={property.id}>
                                {property.name}
                              </Option>
                            ))}
                          </Select>
                        </Row>
                      </Col>
                    )}
                  </Row>
                  {/* Seção: Rebanho */}
                  <CustomDivider
                    title={translation.boitelContractsPage.form.sections.herd}
                  />

                  <Row type="flex" justify="end" style={{ marginBottom: 16 }}>
                    <ButtonStandard
                      buttonType="addNew"
                      type="button"
                      onClick={() => handleAddHerd(values)}
                    >
                      <Icon type="plus" />{" "}
                      {translation.boitelContractsPage.form.addHerd}
                    </ButtonStandard>
                  </Row>

                  <HerdsTable
                    data={values.herds || []}
                    onEdit={handleEditHerd}
                    onRemove={handleRemoveHerd}
                    translation={translation}
                  />

                  {errors.herds && submitCount > 0 && (
                    <Row style={{ marginTop: 8 }}>
                      <label className="error">
                        {translation.boitelContractsPage.form.herdsRequired}
                      </label>
                    </Row>
                  )}
                  {/* Seção: Tipo Contrato */}
                  <CustomDivider
                    title={
                      translation.boitelContractsPage.form.sections.contractType
                    }
                  />

                  <Row type="flex" gutter={16}>
                    <Col span={12}>
                      <Row>
                        <label
                          className={
                            errors.contractType && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.boitelContractsPage.form.contractType}*
                        </label>
                      </Row>
                      <Row>
                        <Select
                          value={values.contractType || undefined}
                          placeholder={
                            translation.boitelContractsPage.form
                              .contractTypePlaceholder
                          }
                          onChange={(value) =>
                            setFieldValue("contractType", value)
                          }
                        >
                          {CONTRACT_TYPES.map((type) => (
                            <Option key={type.value} value={type.value}>
                              {type.label}
                            </Option>
                          ))}
                        </Select>
                      </Row>
                    </Col>
                  </Row>

                  {/* Seção: Contrato Diária */}
                  {values.contractType === "daily" && (
                    <DailyContractCard
                      herds={values.herds || []}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      submitCount={submitCount}
                      translation={translation}
                    />
                  )}

                  {/* Seção: Contrato Consumo MS */}
                  {values.contractType === "dryMatterConsume" && (
                    <DryMatterConsumeCard
                      herds={values.herds || []}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      submitCount={submitCount}
                      translation={translation}
                    />
                  )}

                  {/* Seção: Contrato @ Produzida */}
                  {values.contractType === "arrobaProduction" && (
                    <ArrobaProductionCard
                      herds={values.herds || []}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      submitCount={submitCount}
                      translation={translation}
                    />
                  )}

                  {/* Seção: Contrato Parceria */}
                  {values.contractType === "partnership" && (
                    <PartnershipCard
                      herds={values.herds || []}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      submitCount={submitCount}
                      translation={translation}
                    />
                  )}
                </div>
                <Footer>
                  <Row type="flex">
                    <Col span={24} className="buttonsDiv">
                      <ButtonStandard
                        type="button"
                        buttonType="type7"
                        onClick={handleCancel}
                      >
                        {translation.buttons.cancel}
                      </ButtonStandard>
                      <ButtonStandard
                        type="button"
                        buttonType="type6"
                        onClick={handleSubmit}
                      >
                        {translation.buttons.save}
                      </ButtonStandard>
                    </Col>
                  </Row>
                </Footer>
              </form>
              {/* Modal/Drawer para adicionar/editar rebanho */}
              {isHerdFormVisible && (
                <HerdFormDrawer
                  visible={isHerdFormVisible}
                  data={formHerd}
                  breeds={breedsList}
                  supplements={supplementsList}
                  cattleProducerProperties={cattleProducerProperties}
                  multipleFarm={values.multipleFarm}
                  onSave={handleSaveHerd}
                  onCancel={() => {
                    setIsHerdFormVisible(false);
                    setFormHerd(null);
                  }}
                  translation={translation}
                />
              )}
            </>
          )}
        />
      </Spin>
    </Container>
  );
};

export default BoitelContractsForm;
